import * as Base from '../styles';
import TextInput from 'components/Widget/Input/TextInput';
import { useState, useEffect } from 'react';

interface NumberInputProps {
	label: string;
	value: number;
	onChange?: (value: number) => void;
	readonly?: boolean;
	rightAligned?: boolean; // right align number in input field
	min?: number; // if set, number can be no smaller than this
	max?: number; // if set, number can be no larger than this
	showUpDownArrows?: boolean; // if true, show up/down arrows
}

const NumberInput = ({
	label,
	value,
	onChange,
	readonly,
	rightAligned,
	min,
	max,
	showUpDownArrows,
}: NumberInputProps) => {
	const [displayValue, setDisplayValue] = useState<string>(value.toString());

	// if whoever is using this component changes value from underfoot, update displayValue
	useEffect(() => {
		setDisplayValue(value.toString());
	}, [value]);

	const onInputChange = (inputValue: string) => {
		if (inputValue === '') {
			setDisplayValue(inputValue);
			return;
		}
		// only call onChange if it's a valid number
		let valNum = parseFloat(inputValue);
		if (isNaN(valNum)) return;
		if (min && valNum < min) valNum = min;
		if (max && valNum > max) valNum = max;
		// in the unusual case where we had a single digit, deleted it, and then typed it again
		// the calling component won't change value (since we never send the onChange for the empty string)
		// so we need to manually update displayValue
		if (valNum === value && displayValue === '') {
			setDisplayValue(inputValue);
		}
		onChange?.(valNum);
	};

	const onBlur = () => {
		// when input loses focus, reset displayValue to match value
		// if value is a number, this will have no side effect
		setDisplayValue(value.toString());
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Base.Base readonly={readonly || false}>
			<Base.LetterLabel>{label}</Base.LetterLabel>
			<TextInput
				type="number"
				value={displayValue}
				onChange={(e) => onInputChange(e.target.value)}
				onBlur={onBlur}
				readOnly={readonly ? true : false}
				readonly={readonly}
				textAlign={rightAligned ? 'right' : 'left'}
				showUpDownArrows={showUpDownArrows}
			/>
		</Base.Base>
	);
};

export default NumberInput;
